import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { autobind } from 'core-decorators';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Card from '/@/components/card';
import LocalePreferences from '/@/components/locale-preferences';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { setPreferences } from '/@/store/session/actions';
import { getSessionCountry, getSessionLanguage, getSessionUnits } from '/@/store/session/selectors';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

@connect(
    (state) => ({
        values: {
            country: getSessionCountry(state).id,
            language: getSessionLanguage(state),
            units: getSessionUnits(state),
        },
    }),
    (dispatch) => bindActionCreators({ setPreferences }, dispatch)
)
@autobind
export default class UserPreferences extends Component {
    static contextTypes = {
        history: PropTypes.object,
    };

    state = {
        loading: false,
        values: this.props.values,
        preferencesChanged: false,
    };

    getChangedValues(initialValues, currentValues) {
        const changes = {};

        for (const key in initialValues) {
            if (Object.hasOwn(initialValues, key)) {
                if (currentValues[key] !== initialValues[key]) {
                    changes[key] = currentValues[key];
                }
            }
        }

        for (const key in currentValues) {
            if (Object.hasOwn(currentValues, key)) {
                if (!(key in initialValues)) {
                    changes[key] = currentValues[key];
                }
            }
        }

        return changes;
    }

    handleLocaleChange(localePreferences) {
        this.setState({
            values: {
                ...this.state.values,
                ...localePreferences,
            },
        });
    }

    handleUnitChange(key) {
        return (value) =>
            this.setState({
                values: {
                    ...this.state.values,
                    units: {
                        ...this.state.values.units,
                        [key]: value,
                    },
                },
            });
    }

    handleSubmit() {
        this.setState({ loading: true });
        setTimeout(() => this.setState({ loading: false }, () => this.context.history.goBack()), 1000);
        setTimeout(() => this.props.setPreferences(this.getChangedValues(this.props.values, this.state.values)), 1000);
    }

    componentDidUpdate(_, prevState) {
        if (prevState.values !== this.state.values) {
            if (!isEqual(this.state.values, this.props.values)) {
                this.setState({ preferencesChanged: true });
            } else {
                this.setState({ preferencesChanged: false });
            }
        }
    }

    render() {
        return (
            <div className={styles.root}>
                <Card>
                    <div className={styles.section}>
                        <div className={styles.title}>
                            <span>{_('Localización')}</span>
                        </div>
                        <LocalePreferences
                            persistPlaceholder
                            country={this.state.values.country}
                            language={this.state.values.language}
                            onChange={this.handleLocaleChange}
                            disabled={this.state.loading}
                        />
                    </div>
                    <div className={styles.section}>
                        <div className={styles.title}>
                            <span>{_('Unidades')}</span>
                        </div>
                        <Select
                            color={settings.app.theme}
                            appearance='outline'
                            placeholder={_('Sistema')}
                            persistPlaceholder
                            clearable={false}
                            disabled={this.state.loading}
                            value={this.state.values.units.system}
                            options={settings.choices.unitSystems}
                            onChange={this.handleUnitChange('system')}
                        />
                        <Select
                            color={settings.app.theme}
                            appearance='outline'
                            placeholder={_('Área')}
                            persistPlaceholder
                            clearable={false}
                            disabled={this.state.loading}
                            value={this.state.values.units.area}
                            options={settings.choices.areaUnits}
                            onChange={this.handleUnitChange('area')}
                        />
                        <Select
                            color={settings.app.theme}
                            appearance='outline'
                            placeholder={_('Rendimiento Maíz')}
                            persistPlaceholder
                            clearable={false}
                            disabled={this.state.loading}
                            value={this.state.values.units.cornYield}
                            options={settings.choices.yieldUnits}
                            onChange={this.handleUnitChange('cornYield')}
                        />
                        <Select
                            color={settings.app.theme}
                            appearance='outline'
                            placeholder={_('Densidad Maíz')}
                            persistPlaceholder
                            clearable={false}
                            disabled={this.state.loading}
                            value={this.state.values.units.cornDensity}
                            options={settings.choices.cornDensityUnits}
                            onChange={this.handleUnitChange('cornDensity')}
                        />
                        <Select
                            color={settings.app.theme}
                            appearance='outline'
                            placeholder={_('Rendimiento Soja')}
                            persistPlaceholder
                            clearable={false}
                            disabled={this.state.loading}
                            value={this.state.values.units.soyYield}
                            options={settings.choices.yieldUnits}
                            onChange={this.handleUnitChange('soyYield')}
                        />
                        <Select
                            color={settings.app.theme}
                            appearance='outline'
                            placeholder={_('Densidad Soja')}
                            persistPlaceholder
                            clearable={false}
                            disabled={this.state.loading}
                            value={this.state.values.units.soyDensity}
                            options={settings.choices.soyDensityUnits}
                            onChange={this.handleUnitChange('soyDensity')}
                        />
                    </div>
                    <Stack direction='column' spacing={1}>
                        <Button
                            variant='contained'
                            color='primary'
                            disabled={this.state.loading || !this.state.preferencesChanged}
                            onClick={this.handleSubmit}
                        >
                            {this.state.loading ? _('Guardando...') : _('Guardar')}
                        </Button>
                        <Button variant='outlined' onClick={this.context.history.goBack}>
                            {_('Cancelar')}
                        </Button>
                    </Stack>
                </Card>
            </div>
        );
    }
}
